import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useVaccinesStore } from '../store'
import { usePetsStore } from '../../pets/store'
import type { VaccineCreate } from '@rondela/shared'

export function VaccineFormPage() {
  const { petId, id } = useParams<{ petId: string; id?: string }>()
  const navigate = useNavigate()
  const { vaccines, isLoading, error, fetchVaccinesByPet, createVaccine, updateVaccine, clearError } = useVaccinesStore()
  const { currentPet, fetchPet } = usePetsStore()
  const isEditing = !!id

  const [formData, setFormData] = useState<VaccineCreate>({
    nombre: '',
    fechaAplicacion: new Date().toISOString().split('T')[0] ?? '',
    hora: '',
    fechaProxima: '',
    lote: '',
    veterinario: '',
    notas: '',
  })

  useEffect(() => {
    if (petId) {
      fetchPet(petId)
      fetchVaccinesByPet(petId)
    }
    return () => clearError()
  }, [petId, fetchPet, fetchVaccinesByPet, clearError])

  useEffect(() => {
    if (id && vaccines.length > 0) {
      const vaccine = vaccines.find((v) => v.id === id)
      if (vaccine) {
        setFormData({
          nombre: vaccine.nombre,
          fechaAplicacion: vaccine.fechaAplicacion.split('T')[0] ?? '',
          hora: vaccine.hora ?? '',
          fechaProxima: vaccine.fechaProxima?.split('T')[0] ?? '',
          lote: vaccine.lote ?? '',
          veterinario: vaccine.veterinario ?? '',
          notas: vaccine.notas ?? '',
        })
      }
    }
  }, [id, vaccines])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!petId) return

    try {
      if (isEditing && id) {
        await updateVaccine(id, formData)
      } else {
        await createVaccine(petId, formData)
      }
      navigate(`/mascotas/${petId}`)
    } catch {
      // Error already handled in store
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const commonVaccines = [
    'Rabia',
    'Parvovirus',
    'Moquillo',
    'Hepatitis',
    'Leptospirosis',
    'Bordetella',
    'Triple felina',
    'Leucemia felina',
  ]

  return (
    <div className="page">
      <div className="page-header">
        <Link to={`/mascotas/${petId}`} className="btn btn-ghost">
          &larr; Volver
        </Link>
        <h1>
          {isEditing ? 'Editar vacuna' : 'Nueva vacuna'}
          {currentPet && <span className="text-muted"> - {currentPet.nombre}</span>}
        </h1>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit} className="form">
        <div className="form-group">
          <label htmlFor="nombre">Nombre de la vacuna *</label>
          <input
            type="text"
            id="nombre"
            name="nombre"
            value={formData.nombre}
            onChange={handleChange}
            required
            className="form-control"
            placeholder="Ej: Rabia, Parvovirus..."
            list="vaccine-suggestions"
          />
          <datalist id="vaccine-suggestions">
            {commonVaccines.map((vaccine) => (
              <option key={vaccine} value={vaccine} />
            ))}
          </datalist>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="fechaAplicacion">Fecha de aplicación *</label>
            <input
              type="date"
              id="fechaAplicacion"
              name="fechaAplicacion"
              value={formData.fechaAplicacion}
              onChange={handleChange}
              required
              className="form-control"
            />
          </div>

          <div className="form-group">
            <label htmlFor="hora">Hora</label>
            <input
              type="time"
              id="hora"
              name="hora"
              value={formData.hora}
              onChange={handleChange}
              className="form-control"
            />
          </div>

          <div className="form-group">
            <label htmlFor="fechaProxima">Fecha de refuerzo</label>
            <input
              type="date"
              id="fechaProxima"
              name="fechaProxima"
              value={formData.fechaProxima}
              onChange={handleChange}
              className="form-control"
            />
            <p className="form-help">Próxima fecha de vacunación</p>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="lote">Número de lote</label>
            <input
              type="text"
              id="lote"
              name="lote"
              value={formData.lote}
              onChange={handleChange}
              className="form-control"
              placeholder="Opcional"
            />
          </div>

          <div className="form-group">
            <label htmlFor="veterinario">Veterinario</label>
            <input
              type="text"
              id="veterinario"
              name="veterinario"
              value={formData.veterinario}
              onChange={handleChange}
              className="form-control"
              placeholder="Dr./Dra. ..."
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="notas">Notas</label>
          <textarea
            id="notas"
            name="notas"
            value={formData.notas}
            onChange={handleChange}
            rows={3}
            className="form-control"
            placeholder="Reacciones, observaciones..."
          />
        </div>

        <div className="form-actions">
          <Link to={`/mascotas/${petId}`} className="btn btn-secondary">
            Cancelar
          </Link>
          <button type="submit" className="btn btn-primary" disabled={isLoading}>
            {isLoading ? 'Guardando...' : isEditing ? 'Guardar cambios' : 'Registrar vacuna'}
          </button>
        </div>
      </form>
    </div>
  )
}
